import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, GraduationCap, Image, FileText, Mail, 
  Settings, LogOut, Plus, Trash2, Edit, Eye, EyeOff, Check, X,
  Menu, ChevronRight, BarChart3, TrendingUp, Users, Monitor, Smartphone, Tablet,
  UserCircle, Upload, Phone, Award, Handshake, Globe, ExternalLink,
  Calendar, MapPin, Clock, Euro, Star, MessageSquare, ClipboardList, Filter, Film, Radio, Mic2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import axios from 'axios';

// ============== ADMIN LAYOUT ==============
const AdminDashboard = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Statistiques' },
    { path: '/admin/inscriptions', icon: ClipboardList, label: 'Inscriptions' },
    { path: '/admin/team', icon: UserCircle, label: 'Équipe' },
    { path: '/admin/trainers', icon: Award, label: 'Formateurs' },
    { path: '/admin/partners', icon: Handshake, label: 'Partenaires' },
    { path: '/admin/events', icon: Calendar, label: 'Événements' },
    { path: '/admin/reviews', icon: Star, label: 'Avis' },
    { path: '/admin/documentary', icon: Film, label: 'Documentaire' },
    { path: '/admin/radio', icon: Radio, label: 'Radio' },
    { path: '/admin/trainings', icon: GraduationCap, label: 'Formations' },
    { path: '/admin/gallery', icon: Image, label: 'Galerie' },
    { path: '/admin/quotes', icon: FileText, label: 'Devis' },
    { path: '/admin/messages', icon: Mail, label: 'Messages' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#09090b] border-r border-white/10 transform transition-transform duration-200 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="font-['Teko'] text-2xl text-[#FACC15]">
            STUNT DIRECTOR
          </Link>
          <p className="font-['Barlow'] text-gray-500 text-xs uppercase tracking-wider mt-1">
            Administration
          </p>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 font-['Barlow'] text-sm transition-colors ${
                isActive(item.path)
                  ? 'bg-[#FACC15]/10 text-[#FACC15] border-l-2 border-[#FACC15]'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-[#EF4444] hover:bg-[#EF4444]/10"
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 bg-[#09090b] border-b border-white/10 p-4 flex items-center gap-4 z-30">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="text-white"
          >
            <Menu size={24} />
          </Button>
          <span className="font-['Teko'] text-xl text-[#FACC15]">STUNT DIRECTOR</span>
        </div>

        <div className="p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardHome token={token} />} />
            <Route path="analytics" element={<AnalyticsManager token={token} />} />
            <Route path="inscriptions" element={<RegistrationsManager token={token} />} />
            <Route path="team" element={<TeamManager token={token} />} />
            <Route path="trainers" element={<TrainersManager token={token} />} />
            <Route path="partners" element={<PartnersManager token={token} />} />
            <Route path="events" element={<EventsManager token={token} />} />
            <Route path="reviews" element={<ReviewsManager token={token} />} />
            <Route path="documentary" element={<DocumentaryManager token={token} />} />
            <Route path="radio" element={<RadioManager token={token} />} />
            <Route path="trainings" element={<TrainingsManager token={token} />} />
            <Route path="gallery" element={<GalleryManager token={token} />} />
            <Route path="quotes" element={<QuotesManager token={token} />} />
            <Route path="messages" element={<MessagesManager token={token} />} />
            <Route path="settings" element={<SettingsManager token={token} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

// ============== DASHBOARD HOME ==============
const DashboardHome = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = stats ? [
    { label: 'Formations actives', value: stats.trainings, color: '#FACC15' },
    { label: 'Inscriptions', value: stats.registrations, color: '#22C55E' },
    { label: 'Demandes de devis', value: stats.quotes, color: '#3B82F6' },
    { label: 'Messages non lus', value: stats.unread_messages, color: '#EF4444' },
    { label: 'Éléments galerie', value: stats.gallery_items, color: '#8B5CF6' },
    { label: 'Revenus total', value: `${stats.total_revenue}€`, color: '#FACC15' },
  ] : [];

  return (
    <div>
      <h1 className="font-['Teko'] text-4xl text-white mb-8">TABLEAU DE BORD</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat) => (
            <div 
              key={stat.label}
              className="bg-[#09090b] border border-white/10 p-6"
              data-testid={`stat-card-${stat.label}`}
            >
              <p className="font-['Barlow'] text-gray-400 text-sm uppercase tracking-wider mb-2">
                {stat.label}
              </p>
              <p className="font-['Teko'] text-4xl" style={{ color: stat.color }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="/admin/trainings" className="bg-[#09090b] border border-white/10 p-6 hover:border-[#FACC15]/30 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <GraduationCap size={24} className="text-[#FACC15] mb-4" />
              <h3 className="font-['Teko'] text-2xl text-white">GÉRER LES FORMATIONS</h3>
              <p className="font-['Barlow'] text-gray-400 text-sm mt-1">Ajouter, modifier ou supprimer des formations</p>
            </div>
            <ChevronRight className="text-gray-500 group-hover:text-[#FACC15] transition-colors" />
          </div>
        </Link>
        <Link to="/admin/gallery" className="bg-[#09090b] border border-white/10 p-6 hover:border-[#FACC15]/30 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <Image size={24} className="text-[#FACC15] mb-4" />
              <h3 className="font-['Teko'] text-2xl text-white">GÉRER LA GALERIE</h3>
              <p className="font-['Barlow'] text-gray-400 text-sm mt-1">Ajouter des photos et vidéos</p>
            </div>
            <ChevronRight className="text-gray-500 group-hover:text-[#FACC15] transition-colors" />
          </div>
        </Link>
      </div>
    </div>
  );
};

// ============== ANALYTICS MANAGER ==============
const AnalyticsManager = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone size={18} className="text-[#FACC15]" />;
      case 'tablet': return <Tablet size={18} className="text-[#FACC15]" />;
      default: return <Monitor size={18} className="text-[#FACC15]" />;
    }
  };

  const formatPageName = (page) => {
    const pageNames = {
      '/': 'Accueil',
      '/gallery': 'Galerie',
      '/trainings': 'Formations',
      '/services': 'Services',
      '/contact': 'Contact',
      '/documentaire': 'Documentaire',
    };
    return pageNames[page] || page;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">STATISTIQUES</h1>
        <Button 
          onClick={fetchAnalytics} 
          variant="outline" 
          className="border-white/20 text-white hover:border-[#FACC15]"
        >
          Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : analytics ? (
        <div className="space-y-8">
          {/* Main Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#09090b] border border-white/10 p-6" data-testid="stat-total-views">
              <div className="flex items-center gap-2 mb-2">
                <Eye size={18} className="text-[#FACC15]" />
                <p className="font-['Barlow'] text-gray-400 text-sm uppercase tracking-wider">
                  Vues totales
                </p>
              </div>
              <p className="font-['Teko'] text-4xl text-[#FACC15]">{analytics.total_views}</p>
            </div>
            
            <div className="bg-[#09090b] border border-white/10 p-6" data-testid="stat-today">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-[#22C55E]" />
                <p className="font-['Barlow'] text-gray-400 text-sm uppercase tracking-wider">
                  Aujourd'hui
                </p>
              </div>
              <p className="font-['Teko'] text-4xl text-[#22C55E]">{analytics.views_today}</p>
            </div>
            
            <div className="bg-[#09090b] border border-white/10 p-6" data-testid="stat-week">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 size={18} className="text-[#3B82F6]" />
                <p className="font-['Barlow'] text-gray-400 text-sm uppercase tracking-wider">
                  Cette semaine
                </p>
              </div>
              <p className="font-['Teko'] text-4xl text-[#3B82F6]">{analytics.views_week}</p>
            </div>
            
            <div className="bg-[#09090b] border border-white/10 p-6" data-testid="stat-visitors">
              <div className="flex items-center gap-2 mb-2">
                <Users size={18} className="text-[#8B5CF6]" />
                <p className="font-['Barlow'] text-gray-400 text-sm uppercase tracking-wider">
                  Visiteurs uniques
                </p>
              </div>
              <p className="font-['Teko'] text-4xl text-[#8B5CF6]">{analytics.unique_visitors}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Views Chart */}
            <div className="bg-[#09090b] border border-white/10 p-6">
              <h2 className="font-['Teko'] text-2xl text-white mb-4">VISITES PAR JOUR</h2>
              {analytics.daily_views && analytics.daily_views.length > 0 ? (
                <div className="space-y-2">
                  {analytics.daily_views.map((day) => {
                    const maxViews = Math.max(...analytics.daily_views.map(d => d.views));
                    const percentage = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                    return (
                      <div key={day.date} className="flex items-center gap-3">
                        <span className="font-['Barlow'] text-gray-400 text-sm w-24">
                          {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                        </span>
                        <div className="flex-1 bg-[#18181b] h-6 relative">
                          <div 
                            className="h-full bg-[#FACC15] transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-['Barlow'] text-white text-sm">
                            {day.views}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 font-['Barlow']">Pas encore de données</p>
              )}
            </div>

            {/* Top Pages */}
            <div className="bg-[#09090b] border border-white/10 p-6">
              <h2 className="font-['Teko'] text-2xl text-white mb-4">PAGES LES PLUS VISITÉES</h2>
              {analytics.top_pages && analytics.top_pages.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_pages.map((page, idx) => (
                    <div key={page.page} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="font-['Teko'] text-xl text-[#FACC15] w-6">{idx + 1}</span>
                        <span className="font-['Barlow'] text-white">{formatPageName(page.page)}</span>
                      </div>
                      <span className="font-['Barlow'] text-gray-400">{page.views} vues</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 font-['Barlow']">Pas encore de données</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device Stats */}
            <div className="bg-[#09090b] border border-white/10 p-6">
              <h2 className="font-['Teko'] text-2xl text-white mb-4">APPAREILS</h2>
              {analytics.device_stats && Object.keys(analytics.device_stats).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(analytics.device_stats).map(([device, count]) => {
                    const total = Object.values(analytics.device_stats).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={device} className="flex items-center gap-4">
                        {getDeviceIcon(device)}
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="font-['Barlow'] text-white capitalize">{device}</span>
                            <span className="font-['Barlow'] text-gray-400">{percentage}%</span>
                          </div>
                          <div className="w-full bg-[#18181b] h-2">
                            <div 
                              className="h-full bg-[#FACC15] transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                        <span className="font-['Barlow'] text-gray-400 w-16 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 font-['Barlow']">Pas encore de données</p>
              )}
            </div>

            {/* Top Referrers */}
            <div className="bg-[#09090b] border border-white/10 p-6">
              <h2 className="font-['Teko'] text-2xl text-white mb-4">SOURCES DE TRAFIC</h2>
              {analytics.top_referrers && analytics.top_referrers.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_referrers.map((ref) => (
                    <div key={ref.referrer || 'direct'} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="font-['Barlow'] text-white truncate max-w-[200px]">
                        {ref.referrer ? ref.referrer.replace(/^https?:\/\//, '').split('/')[0] : 'Direct'}
                      </span>
                      <span className="font-['Barlow'] text-gray-400">{ref.count} visites</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 font-['Barlow']">Trafic direct uniquement</p>
              )}
            </div>
          </div>

          {/* Month Stats */}
          <div className="bg-[#09090b] border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-['Barlow'] text-gray-400 text-sm uppercase tracking-wider mb-1">
                  Vues ce mois
                </p>
                <p className="font-['Teko'] text-5xl text-white">{analytics.views_month}</p>
              </div>
              <div className="text-right">
                <p className="font-['Barlow'] text-gray-400 text-sm">
                  Moyenne journalière
                </p>
                <p className="font-['Teko'] text-3xl text-[#FACC15]">
                  {analytics.views_month > 0 ? Math.round(analytics.views_month / 30) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-500 py-12 font-['Barlow']">Erreur de chargement des statistiques</p>
      )}
    </div>
  );
};

// ============== REGISTRATIONS MANAGER ==============
const RegistrationsManager = ({ token }) => {
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [trainingRegistrations, setTrainingRegistrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'events', 'trainings'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'cancelled', 'paid'

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [eventRegsRes, trainingRegsRes, eventsRes, trainingsRes] = await Promise.all([
        axios.get(`${API}/event-registrations/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/registrations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trainings/all`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setEventRegistrations(eventRegsRes.data || []);
      setTrainingRegistrations(trainingRegsRes.data || []);
      setEvents(eventsRes.data || []);
      setTrainings(trainingsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.title : 'Événement inconnu';
  };

  const getTrainingTitle = (trainingId) => {
    const training = trainings.find(t => t.id === trainingId);
    return training ? training.title : 'Formation inconnue';
  };

  const updateEventRegistrationStatus = async (regId, status) => {
    try {
      await axios.put(`${API}/event-registrations/${regId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteEventRegistration = async (regId) => {
    if (!window.confirm('Supprimer cette inscription ?')) return;
    try {
      await axios.delete(`${API}/event-registrations/${regId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Inscription supprimée');
      fetchAllData();
    } catch (error) { toast.error('Erreur'); }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      confirmed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      paid: 'bg-green-500/20 text-green-400',
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      paid: 'Payé',
    };
    return <span className={`text-xs px-2 py-1 rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>{labels[status] || status}</span>;
  };

  // Combine and filter registrations
  const allRegistrations = [
    ...eventRegistrations.map(r => ({ ...r, type: 'event', title: getEventTitle(r.event_id) })),
    ...trainingRegistrations.map(r => ({ ...r, type: 'training', title: getTrainingTitle(r.training_id), status: r.payment_status })),
  ].filter(r => {
    if (filter !== 'all' && r.type !== (filter === 'events' ? 'event' : 'training')) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const stats = {
    total: eventRegistrations.length + trainingRegistrations.length,
    events: eventRegistrations.length,
    trainings: trainingRegistrations.length,
    pending: [...eventRegistrations, ...trainingRegistrations].filter(r => r.status === 'pending' || r.payment_status === 'pending').length,
    confirmed: eventRegistrations.filter(r => r.status === 'confirmed').length + trainingRegistrations.filter(r => r.payment_status === 'paid').length,
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-['Teko'] text-4xl text-white">INSCRIPTIONS</h1>
          <p className="font-['Barlow'] text-gray-400 text-sm mt-1">Gérez toutes les inscriptions aux événements et formations</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-[#09090b] border border-white/10 p-4">
          <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Total</p>
          <p className="font-['Teko'] text-3xl text-white">{stats.total}</p>
        </div>
        <div className="bg-[#09090b] border border-white/10 p-4">
          <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Événements</p>
          <p className="font-['Teko'] text-3xl text-[#FACC15]">{stats.events}</p>
        </div>
        <div className="bg-[#09090b] border border-white/10 p-4">
          <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Formations</p>
          <p className="font-['Teko'] text-3xl text-blue-400">{stats.trainings}</p>
        </div>
        <div className="bg-[#09090b] border border-white/10 p-4">
          <p className="font-['Barlow'] text-gray-400 text-xs uppercase">En attente</p>
          <p className="font-['Teko'] text-3xl text-yellow-400">{stats.pending}</p>
        </div>
        <div className="bg-[#09090b] border border-white/10 p-4">
          <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Confirmées</p>
          <p className="font-['Teko'] text-3xl text-green-400">{stats.confirmed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#09090b] border border-white/20 text-white text-sm p-2 rounded"
          >
            <option value="all">Tous les types</option>
            <option value="events">Événements</option>
            <option value="trainings">Formations</option>
          </select>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#09090b] border border-white/20 text-white text-sm p-2 rounded"
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmé</option>
          <option value="paid">Payé</option>
          <option value="cancelled">Annulé</option>
        </select>
      </div>

      {/* Registrations List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : allRegistrations.length === 0 ? (
        <div className="text-center py-12">
          <ClipboardList size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-500 font-['Barlow']">Aucune inscription trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {allRegistrations.map((reg) => (
            <div key={`${reg.type}-${reg.id}`} className="bg-[#09090b] border border-white/10 p-4 hover:border-white/20 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-xs px-2 py-1 rounded font-['Barlow'] uppercase ${reg.type === 'event' ? 'bg-[#FACC15]/20 text-[#FACC15]' : 'bg-blue-500/20 text-blue-400'}`}>
                      {reg.type === 'event' ? 'Événement' : 'Formation'}
                    </span>
                    {getStatusBadge(reg.status)}
                  </div>
                  <h3 className="font-['Teko'] text-xl text-white">{reg.title}</h3>
                  <div className="font-['Barlow'] text-sm text-gray-400 mt-1">
                    <span className="text-white font-medium">{reg.type === 'event' ? reg.name : `${reg.first_name} ${reg.last_name}`}</span>
                    <span className="mx-2">•</span>
                    <span>{reg.email}</span>
                    <span className="mx-2">•</span>
                    <span>{reg.phone}</span>
                  </div>
                  <p className="font-['Barlow'] text-xs text-gray-500 mt-2">
                    Inscrit le {formatDate(reg.created_at)}
                    {reg.amount && <span className="ml-2">• {reg.amount}€</span>}
                  </p>
                  {reg.message && <p className="font-['Barlow'] text-sm text-gray-300 mt-2 italic">"{reg.message}"</p>}
                </div>
                <div className="flex items-center gap-2">
                  {reg.type === 'event' && (
                    <>
                      <select
                        value={reg.status}
                        onChange={(e) => updateEventRegistrationStatus(reg.id, e.target.value)}
                        className="bg-black border border-white/20 text-white text-sm p-2 rounded"
                      >
                        <option value="pending">En attente</option>
                        <option value="confirmed">Confirmé</option>
                        <option value="cancelled">Annulé</option>
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteEventRegistration(reg.id)}
                        className="text-red-400 hover:text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  )}
                  {reg.type === 'training' && (
                    <span className="font-['Barlow'] text-sm text-gray-400">
                      {reg.payment_method === 'stripe' ? 'Paiement Stripe' : 'Paiement en attente'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== TEAM MANAGER ==============
const TeamManager = ({ token }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    role: '',
    specialty: '',
    order: 0,
  });

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data || []);
    } catch (error) {
      console.error('Error:', error);
      // Initialize with default team if empty
      if (error.response?.status === 404 || members.length === 0) {
        initializeDefaultTeam();
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultTeam = async () => {
    const defaultTeam = [
      { name: 'Alexandra CHALLET', phone: '06 29 65 35 24', role: 'Scénariste, Réalisatrice d\'action & documentaire', specialty: '2ème équipe action, Actrice', order: 0 },
      { name: 'André FERRER', phone: '07 68 25 76 00', role: 'Comédien cascadeur', specialty: 'Régleur cascade', order: 1 },
      { name: 'Éric TATIN', phone: '07 86 12 39 12', role: 'Régleur cascade', specialty: 'Spécialisé câblage', order: 2 },
    ];
    
    for (const member of defaultTeam) {
      try {
        await axios.post(`${API}/team`, member, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Error creating default member:', e);
      }
    }
    fetchTeam();
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', role: '', specialty: '', order: 0 });
    setEditingMember(null);
    setPhotoFile(null);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      phone: member.phone,
      role: member.role,
      specialty: member.specialty,
      order: member.order || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let memberId = editingMember?.id;

      if (editingMember) {
        // Update existing member
        await axios.put(`${API}/team/${editingMember.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new member
        const response = await axios.post(`${API}/team`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        memberId = response.data.id;
      }

      // Upload photo if selected
      if (photoFile && memberId) {
        const photoData = new FormData();
        photoData.append('file', photoFile);
        photoData.append('member_id', memberId);
        
        await axios.post(`${API}/upload/team-photo`, photoData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success(editingMember ? 'Membre modifié !' : 'Membre ajouté !');
      setDialogOpen(false);
      resetForm();
      fetchTeam();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    try {
      await axios.delete(`${API}/team/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membre supprimé');
      fetchTeam();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePhotoUpload = async (memberId, file) => {
    const photoData = new FormData();
    photoData.append('file', file);
    photoData.append('member_id', memberId);
    
    try {
      await axios.post(`${API}/upload/team-photo`, photoData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Photo mise à jour !');
      fetchTeam();
    } catch (error) {
      toast.error('Erreur lors de l\'upload de la photo');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">ÉQUIPE</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]" data-testid="add-team-btn">
              <Plus size={18} className="mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#09090b] border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white">
                {editingMember ? 'Modifier le membre' : 'Ajouter un membre'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Photo Upload */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Photo</Label>
                <div className="border-2 border-dashed border-white/20 hover:border-[#FACC15]/50 transition-colors p-4 text-center cursor-pointer relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhotoFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  {photoFile ? (
                    <p className="font-['Barlow'] text-[#FACC15]">{photoFile.name}</p>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={24} className="text-gray-500" />
                      <p className="font-['Barlow'] text-gray-400 text-sm">Cliquez pour ajouter une photo</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Name */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Nom complet</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Prénom NOM"
                  className="bg-black border-white/20 text-white"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="06 12 34 56 78"
                  className="bg-black border-white/20 text-white"
                  required
                />
              </div>

              {/* Role */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Rôle principal</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  placeholder="Ex: Régleur cascade, Comédien cascadeur..."
                  className="bg-black border-white/20 text-white"
                  required
                />
              </div>

              {/* Specialty */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Spécialité</Label>
                <Input
                  value={formData.specialty}
                  onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                  placeholder="Ex: Spécialisé câblage, 2ème équipe action..."
                  className="bg-black border-white/20 text-white"
                />
              </div>

              {/* Order */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  className="bg-black border-white/20 text-white"
                />
              </div>

              <Button 
                type="submit" 
                disabled={uploading}
                className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50"
              >
                {uploading ? 'Enregistrement...' : (editingMember ? 'Modifier' : 'Ajouter')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <UserCircle size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-500 font-['Barlow']">Aucun membre. L'équipe par défaut va être créée...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((member) => (
            <div 
              key={member.id} 
              className="bg-[#09090b] border border-white/10 p-6 hover:border-[#FACC15]/30 transition-colors"
              data-testid={`team-member-${member.id}`}
            >
              {/* Photo */}
              <div className="aspect-[9/16] w-full max-w-[150px] mx-auto mb-4 bg-[#18181b] overflow-hidden relative group">
                {member.photo_url ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${member.photo_url}`}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#FACC15]">
                    <span className="font-['Teko'] text-4xl text-black">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                )}
                {/* Photo upload overlay */}
                <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files[0] && handlePhotoUpload(member.id, e.target.files[0])}
                    className="hidden"
                  />
                  <Upload size={24} className="text-white" />
                </label>
              </div>

              {/* Info */}
              <h3 className="font-['Teko'] text-xl text-white text-center">{member.name}</h3>
              <p className="font-['Barlow'] text-[#FACC15] text-sm text-center uppercase tracking-wider">{member.role}</p>
              <p className="font-['Barlow'] text-gray-400 text-sm text-center mb-4">{member.specialty}</p>
              <p className="font-['Barlow'] text-gray-300 text-sm text-center flex items-center justify-center gap-2">
                <Phone size={14} className="text-[#FACC15]" />
                {member.phone}
              </p>

              {/* Actions */}
              <div className="flex justify-center gap-2 mt-4 pt-4 border-t border-white/10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(member)}
                  className="text-white hover:text-[#FACC15] hover:bg-white/5"
                >
                  <Edit size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(member.id)}
                  className="text-white hover:text-red-500 hover:bg-white/5"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== TRAINERS MANAGER ==============
const TrainersManager = ({ token }) => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    specialty: '',
    bio: '',
    order: 0,
  });

  useEffect(() => { fetchTrainers(); }, []);

  const fetchTrainers = async () => {
    try {
      const response = await axios.get(`${API}/trainers/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainers(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', role: '', specialty: '', bio: '', order: 0 });
    setEditingTrainer(null);
    setPhotoFile(null);
  };

  const handleEdit = (trainer) => {
    setEditingTrainer(trainer);
    setFormData({
      name: trainer.name,
      role: trainer.role,
      specialty: trainer.specialty || '',
      bio: trainer.bio || '',
      order: trainer.order || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let trainerId = editingTrainer?.id;
      if (editingTrainer) {
        await axios.put(`${API}/trainers/${editingTrainer.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API}/trainers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        trainerId = response.data.id;
      }
      if (photoFile && trainerId) {
        const photoData = new FormData();
        photoData.append('file', photoFile);
        photoData.append('trainer_id', trainerId);
        await axios.post(`${API}/upload/trainer-photo`, photoData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(editingTrainer ? 'Formateur modifié !' : 'Formateur ajouté !');
      setDialogOpen(false);
      resetForm();
      fetchTrainers();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce formateur ?')) return;
    try {
      await axios.delete(`${API}/trainers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Formateur supprimé');
      fetchTrainers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePhotoUpload = async (trainerId, file) => {
    const photoData = new FormData();
    photoData.append('file', file);
    photoData.append('trainer_id', trainerId);
    try {
      await axios.post(`${API}/upload/trainer-photo`, photoData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo mise à jour !');
      fetchTrainers();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">FORMATEURS</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]" data-testid="add-trainer-btn">
              <Plus size={18} className="mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#09090b] border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white">
                {editingTrainer ? 'Modifier le formateur' : 'Ajouter un formateur'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Photo</Label>
                <div className="border-2 border-dashed border-white/20 hover:border-[#FACC15]/50 p-4 text-center cursor-pointer relative">
                  <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  {photoFile ? <p className="font-['Barlow'] text-[#FACC15]">{photoFile.name}</p> : <div className="flex flex-col items-center gap-2"><Upload size={24} className="text-gray-500" /><p className="font-['Barlow'] text-gray-400 text-sm">Cliquez pour ajouter une photo</p></div>}
                </div>
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Nom complet</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Prénom NOM" className="bg-black border-white/20 text-white" required />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Fonction</Label>
                <Input value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="Ex: Formateur combat, Coordinateur cascade..." className="bg-black border-white/20 text-white" required />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Spécialité</Label>
                <Input value={formData.specialty} onChange={(e) => setFormData({...formData, specialty: e.target.value})} placeholder="Ex: Arts martiaux, Câblage..." className="bg-black border-white/20 text-white" />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Bio</Label>
                <Textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} placeholder="Courte description..." className="bg-black border-white/20 text-white" rows={3} />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Ordre</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="bg-black border-white/20 text-white" />
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50">
                {uploading ? 'Enregistrement...' : (editingTrainer ? 'Modifier' : 'Ajouter')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : trainers.length === 0 ? (
        <div className="text-center py-12">
          <Award size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-500 font-['Barlow']">Aucun formateur. Cliquez sur "Ajouter" pour en créer un.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trainers.map((trainer) => (
            <div key={trainer.id} className="bg-[#09090b] border border-white/10 p-6 hover:border-[#FACC15]/30 transition-colors">
              <div className="aspect-[9/16] w-full max-w-[150px] mx-auto mb-4 bg-[#18181b] overflow-hidden relative group">
                {trainer.photo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${trainer.photo_url}`} alt={trainer.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#FACC15]">
                    <span className="font-['Teko'] text-4xl text-black">{trainer.name.split(' ').map(n => n[0]).join('')}</span>
                  </div>
                )}
                <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handlePhotoUpload(trainer.id, e.target.files[0])} className="hidden" />
                  <Upload size={24} className="text-white" />
                </label>
              </div>
              <h3 className="font-['Teko'] text-xl text-white text-center">{trainer.name}</h3>
              <p className="font-['Barlow'] text-[#FACC15] text-sm text-center uppercase tracking-wider">{trainer.role}</p>
              {trainer.specialty && <p className="font-['Barlow'] text-gray-400 text-sm text-center">{trainer.specialty}</p>}
              {trainer.bio && <p className="font-['Barlow'] text-gray-300 text-xs text-center mt-2 line-clamp-2">{trainer.bio}</p>}
              <div className="flex justify-center gap-2 mt-4 pt-4 border-t border-white/10">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(trainer)} className="text-white hover:text-[#FACC15] hover:bg-white/5"><Edit size={16} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(trainer.id)} className="text-white hover:text-red-500 hover:bg-white/5"><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== PARTNERS MANAGER ==============
const PartnersManager = ({ token }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    order: 0,
  });

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', website: '', order: 0 });
    setEditingPartner(null);
    setLogoFile(null);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      description: partner.description || '',
      website: partner.website || '',
      order: partner.order || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let partnerId = editingPartner?.id;
      if (editingPartner) {
        await axios.put(`${API}/partners/${editingPartner.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API}/partners`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        partnerId = response.data.id;
      }
      if (logoFile && partnerId) {
        const logoData = new FormData();
        logoData.append('file', logoFile);
        logoData.append('partner_id', partnerId);
        await axios.post(`${API}/upload/partner-logo`, logoData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(editingPartner ? 'Partenaire modifié !' : 'Partenaire ajouté !');
      setDialogOpen(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await axios.delete(`${API}/partners/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Partenaire supprimé');
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLogoUpload = async (partnerId, file) => {
    const logoData = new FormData();
    logoData.append('file', file);
    logoData.append('partner_id', partnerId);
    try {
      await axios.post(`${API}/upload/partner-logo`, logoData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo mis à jour !');
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">PARTENAIRES</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]" data-testid="add-partner-btn">
              <Plus size={18} className="mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#09090b] border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white">
                {editingPartner ? 'Modifier le partenaire' : 'Ajouter un partenaire'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Logo</Label>
                <div className="border-2 border-dashed border-white/20 hover:border-[#FACC15]/50 p-4 text-center cursor-pointer relative">
                  <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  {logoFile ? <p className="font-['Barlow'] text-[#FACC15]">{logoFile.name}</p> : <div className="flex flex-col items-center gap-2"><Upload size={24} className="text-gray-500" /><p className="font-['Barlow'] text-gray-400 text-sm">Cliquez pour ajouter un logo</p></div>}
                </div>
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Nom</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Nom du partenaire" className="bg-black border-white/20 text-white" required />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Description du partenariat..." className="bg-black border-white/20 text-white" rows={3} />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Site web</Label>
                <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="https://..." className="bg-black border-white/20 text-white" />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Ordre</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="bg-black border-white/20 text-white" />
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50">
                {uploading ? 'Enregistrement...' : (editingPartner ? 'Modifier' : 'Ajouter')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : partners.length === 0 ? (
        <div className="text-center py-12">
          <Handshake size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-500 font-['Barlow']">Aucun partenaire. Cliquez sur "Ajouter" pour en créer un.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((partner) => (
            <div key={partner.id} className="bg-[#09090b] border border-white/10 p-6 hover:border-[#FACC15]/30 transition-colors">
              <div className="aspect-square w-full max-w-[150px] mx-auto mb-4 bg-[#18181b] overflow-hidden relative group flex items-center justify-center">
                {partner.logo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}`} alt={partner.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/5">
                    <Handshake size={48} className="text-[#FACC15]" />
                  </div>
                )}
                <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleLogoUpload(partner.id, e.target.files[0])} className="hidden" />
                  <Upload size={24} className="text-white" />
                </label>
              </div>
              <h3 className="font-['Teko'] text-xl text-white text-center">{partner.name}</h3>
              {partner.description && <p className="font-['Barlow'] text-gray-400 text-sm text-center mt-2 line-clamp-2">{partner.description}</p>}
              {partner.website && (
                <a href={partner.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-[#FACC15] text-sm mt-2 hover:underline">
                  <Globe size={14} /> Site web <ExternalLink size={12} />
                </a>
              )}
              <div className="flex justify-center gap-2 mt-4 pt-4 border-t border-white/10">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(partner)} className="text-white hover:text-[#FACC15] hover:bg-white/5"><Edit size={16} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(partner.id)} className="text-white hover:text-red-500 hover:bg-white/5"><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== EVENTS MANAGER ==============
const EventsManager = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [registrationsDialogOpen, setRegistrationsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', date: '', time: '', location: '', max_participants: '', price: 0
  });

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/all`, { headers: { Authorization: `Bearer ${token}` } });
      setEvents(response.data || []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchRegistrations = async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/registrations`, { headers: { Authorization: `Bearer ${token}` } });
      setRegistrations(response.data || []);
    } catch (error) { console.error('Error:', error); }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', date: '', time: '', location: '', max_participants: '', price: 0 });
    setEditingEvent(null);
    setImageFile(null);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title, description: event.description, date: event.date,
      time: event.time || '', location: event.location,
      max_participants: event.max_participants || '', price: event.price || 0
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const data = { ...formData, max_participants: formData.max_participants ? parseInt(formData.max_participants) : null, price: parseFloat(formData.price) || 0 };
      let eventId = editingEvent?.id;
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const response = await axios.post(`${API}/events`, data, { headers: { Authorization: `Bearer ${token}` } });
        eventId = response.data.id;
      }
      if (imageFile && eventId) {
        const imgData = new FormData();
        imgData.append('file', imageFile);
        imgData.append('event_id', eventId);
        await axios.post(`${API}/upload/event-image`, imgData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(editingEvent ? 'Événement modifié !' : 'Événement créé !');
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) { toast.error('Erreur'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Événement supprimé');
      fetchEvents();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleToggle = async (id) => {
    try {
      const response = await axios.put(`${API}/events/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.active ? 'Événement activé' : 'Événement désactivé');
      fetchEvents();
    } catch (error) { toast.error('Erreur'); }
  };

  const showRegistrations = (event) => {
    setSelectedEvent(event);
    fetchRegistrations(event.id);
    setRegistrationsDialogOpen(true);
  };

  const updateRegistrationStatus = async (regId, status) => {
    try {
      await axios.put(`${API}/event-registrations/${regId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Statut mis à jour');
      fetchRegistrations(selectedEvent.id);
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteRegistration = async (regId) => {
    if (!window.confirm('Supprimer cette inscription ?')) return;
    try {
      await axios.delete(`${API}/event-registrations/${regId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Inscription supprimée');
      fetchRegistrations(selectedEvent.id);
    } catch (error) { toast.error('Erreur'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">ÉVÉNEMENTS</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]"><Plus size={18} className="mr-2" /> Ajouter</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#09090b] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-['Teko'] text-2xl text-white">{editingEvent ? 'Modifier' : 'Créer un événement'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Image</Label>
                <div className="border-2 border-dashed border-white/20 hover:border-[#FACC15]/50 p-4 text-center cursor-pointer relative">
                  <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  {imageFile ? <p className="font-['Barlow'] text-[#FACC15]">{imageFile.name}</p> : <p className="font-['Barlow'] text-gray-400 text-sm">Cliquez pour ajouter une image</p>}
                </div>
              </div>
              <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Titre *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-black border-white/20 text-white" required /></div>
              <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-black border-white/20 text-white" rows={3} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Date *</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="bg-black border-white/20 text-white" required /></div>
                <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Heure</Label><Input type="time" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="bg-black border-white/20 text-white" /></div>
              </div>
              <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Lieu *</Label><Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="bg-black border-white/20 text-white" required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Places max</Label><Input type="number" value={formData.max_participants} onChange={(e) => setFormData({...formData, max_participants: e.target.value})} className="bg-black border-white/20 text-white" placeholder="Illimité" /></div>
                <div><Label className="text-[#FACC15] text-sm uppercase mb-2 block">Prix (€)</Label><Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-black border-white/20 text-white" /></div>
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50">{uploading ? 'Enregistrement...' : (editingEvent ? 'Modifier' : 'Créer')}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : events.length === 0 ? (
        <div className="text-center py-12"><Calendar size={48} className="mx-auto text-gray-600 mb-4" /><p className="text-gray-500 font-['Barlow']">Aucun événement</p></div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className={`bg-[#09090b] border p-6 ${event.active ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-['Teko'] text-2xl text-white">{event.title}</h3>
                    {!event.active && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Inactif</span>}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400 font-['Barlow']">
                    <span className="flex items-center gap-1"><Calendar size={14} className="text-[#FACC15]" />{event.date}</span>
                    {event.time && <span className="flex items-center gap-1"><Clock size={14} />{event.time}</span>}
                    <span className="flex items-center gap-1"><MapPin size={14} />{event.location}</span>
                    {event.max_participants && <span className="flex items-center gap-1"><Users size={14} />{event.max_participants} places</span>}
                    {event.price > 0 && <span className="flex items-center gap-1"><Euro size={14} />{event.price}€</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => showRegistrations(event)} className="text-[#FACC15] hover:bg-white/5"><Users size={16} className="mr-1" />Inscriptions</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleToggle(event.id)} className="text-gray-400 hover:text-white hover:bg-white/5">{event.active ? <EyeOff size={16} /> : <Eye size={16} />}</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(event)} className="text-white hover:text-[#FACC15] hover:bg-white/5"><Edit size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)} className="text-white hover:text-red-500 hover:bg-white/5"><Trash2 size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Registrations Dialog */}
      <Dialog open={registrationsDialogOpen} onOpenChange={setRegistrationsDialogOpen}>
        <DialogContent className="bg-[#09090b] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Teko'] text-2xl text-white">Inscriptions - {selectedEvent?.title}</DialogTitle></DialogHeader>
          {registrations.length === 0 ? (
            <p className="text-gray-500 font-['Barlow'] text-center py-8">Aucune inscription</p>
          ) : (
            <div className="space-y-3 mt-4">
              {registrations.map((reg) => (
                <div key={reg.id} className="bg-black/50 border border-white/10 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-['Teko'] text-lg text-white">{reg.name}</p>
                      <p className="font-['Barlow'] text-gray-400 text-sm">{reg.email} • {reg.phone}</p>
                      {reg.message && <p className="font-['Barlow'] text-gray-300 text-sm mt-2">{reg.message}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={reg.status} onChange={(e) => updateRegistrationStatus(reg.id, e.target.value)} className="bg-black border border-white/20 text-white text-sm p-1 rounded">
                        <option value="pending">En attente</option>
                        <option value="confirmed">Confirmé</option>
                        <option value="cancelled">Annulé</option>
                      </select>
                      <Button variant="ghost" size="sm" onClick={() => deleteRegistration(reg.id)} className="text-red-400 hover:text-red-500"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== REVIEWS MANAGER ==============
const ReviewsManager = ({ token }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/all`, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(response.data || []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id, approved) => {
    try {
      await axios.put(`${API}/reviews/${id}/approve?approved=${approved}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(approved ? 'Avis approuvé' : 'Avis masqué');
      fetchReviews();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Avis supprimé');
      fetchReviews();
    } catch (error) { toast.error('Erreur'); }
  };

  const StarDisplay = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} size={16} className={star <= rating ? 'fill-[#FACC15] text-[#FACC15]' : 'text-gray-600'} />
      ))}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">AVIS CLIENTS</h1>
        <div className="flex gap-4 text-sm font-['Barlow']">
          <span className="text-green-400">✓ {reviews.filter(r => r.approved).length} approuvés</span>
          <span className="text-yellow-400">⏳ {reviews.filter(r => !r.approved).length} en attente</span>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : reviews.length === 0 ? (
        <div className="text-center py-12"><MessageSquare size={48} className="mx-auto text-gray-600 mb-4" /><p className="text-gray-500 font-['Barlow']">Aucun avis</p></div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className={`bg-[#09090b] border p-6 ${review.approved ? 'border-green-500/30' : 'border-yellow-500/30'}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <span className="font-['Teko'] text-xl text-white">{review.name}</span>
                    <StarDisplay rating={review.rating} />
                    <span className={`text-xs px-2 py-1 rounded ${review.approved ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {review.approved ? 'Publié' : 'En attente'}
                    </span>
                  </div>
                  <p className="font-['Barlow'] text-gray-300 mb-2">{review.comment}</p>
                  <p className="font-['Barlow'] text-gray-500 text-xs">
                    {new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {review.email && ` • ${review.email}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!review.approved ? (
                    <Button variant="ghost" size="sm" onClick={() => handleApprove(review.id, true)} className="text-green-400 hover:text-green-500 hover:bg-white/5"><Check size={16} className="mr-1" />Approuver</Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => handleApprove(review.id, false)} className="text-yellow-400 hover:text-yellow-500 hover:bg-white/5"><EyeOff size={16} className="mr-1" />Masquer</Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(review.id)} className="text-white hover:text-red-500 hover:bg-white/5"><Trash2 size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== DOCUMENTARY MANAGER ==============
const DocumentaryManager = ({ token }) => {
  const [content, setContent] = useState({
    bio: '',
    subtitle: 'Réalisatrice · Scénariste · 2ème équipe action · Actrice',
    intro_text: '',
    photo_url: null,
    video_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/documentary`);
      setContent(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/documentary`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contenu documentaire mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      const response = await axios.post(`${API}/upload/documentary-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setContent({ ...content, photo_url: response.data.url });
      setPhotoFile(null);
      toast.success('Photo uploadée !');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-['Teko'] text-4xl text-white">DOCUMENTAIRE</h1>
          <p className="font-['Barlow'] text-gray-400 text-sm mt-1">Gérez le contenu de la page Documentaire d'Alexandra CHALLET</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Photo Section */}
          <div className="lg:col-span-1">
            <div className="bg-[#09090b] border border-white/10 p-6">
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-4 block">Photo d'Alexandra</Label>
              
              <div className="aspect-square bg-black/50 border border-white/10 mb-4 flex items-center justify-center overflow-hidden">
                {content.photo_url ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}${content.photo_url}`} 
                    alt="Alexandra Challet" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-['Teko'] text-6xl text-[#FACC15]">AC</span>
                )}
              </div>
              
              <div className="space-y-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files[0])}
                  className="bg-black border-white/20 text-white text-sm"
                />
                {photoFile && (
                  <Button
                    onClick={handlePhotoUpload}
                    disabled={uploading}
                    className="w-full bg-white/10 hover:bg-white/20 text-white"
                  >
                    {uploading ? 'Upload...' : 'Uploader la photo'}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#09090b] border border-white/10 p-6">
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Sous-titre / Rôles</Label>
              <Input
                value={content.subtitle}
                onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
                className="bg-black border-white/20 text-white"
                placeholder="Réalisatrice · Scénariste · ..."
              />
            </div>

            <div className="bg-[#09090b] border border-white/10 p-6">
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Texte d'introduction (Hero)</Label>
              <Textarea
                value={content.intro_text}
                onChange={(e) => setContent({ ...content, intro_text: e.target.value })}
                className="bg-black border-white/20 text-white"
                rows={4}
                placeholder="Réalisatrice de documentaires, Alexandra CHALLET met son expertise..."
              />
              <p className="font-['Barlow'] text-gray-500 text-xs mt-2">Ce texte apparaît dans la section hero en haut de la page.</p>
            </div>

            <div className="bg-[#09090b] border border-white/10 p-6">
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Biographie complète</Label>
              <Textarea
                value={content.bio}
                onChange={(e) => setContent({ ...content, bio: e.target.value })}
                className="bg-black border-white/20 text-white"
                rows={8}
                placeholder="Avec plus de 15 ans d'expérience dans l'industrie du cinéma, Alexandra combine sa passion pour l'action et le documentaire..."
              />
              <p className="font-['Barlow'] text-gray-500 text-xs mt-2">Cette biographie apparaît dans la section profil d'Alexandra.</p>
            </div>

            <div className="bg-[#09090b] border border-white/10 p-6">
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">URL Vidéo YouTube (optionnel)</Label>
              <Input
                value={content.video_url || ''}
                onChange={(e) => setContent({ ...content, video_url: e.target.value })}
                className="bg-black border-white/20 text-white"
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="font-['Barlow'] text-gray-500 text-xs mt-2">Ajoutez une vidéo de présentation ou un extrait de documentaire.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== RADIO MANAGER ==============
const RadioManager = ({ token }) => {
  const [content, setContent] = useState({
    title: 'STUNT RADIO',
    slogan: 'ON AIR · ON RIDE · ON FIRE',
    description: '',
    radioking_url: 'https://www.radioking.com/radio/stunt-radio',
    logo_url: '/images/stunt-radio-logo.jpg'
  });
  const [hosts, setHosts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [hostForm, setHostForm] = useState({ name: '', role: 'Animateur', bio: '' });
  const [programForm, setProgramForm] = useState({
    title: '', description: '', host_name: '', day: 'lundi',
    start_time: '09:00', end_time: '10:00', color: '#FACC15'
  });
  const [activeTab, setActiveTab] = useState('content'); // content, hosts, programs

  const DAYS = [
    { value: 'lundi', label: 'Lundi' },
    { value: 'mardi', label: 'Mardi' },
    { value: 'mercredi', label: 'Mercredi' },
    { value: 'jeudi', label: 'Jeudi' },
    { value: 'vendredi', label: 'Vendredi' },
    { value: 'samedi', label: 'Samedi' },
    { value: 'dimanche', label: 'Dimanche' },
    { value: 'tous', label: 'Tous les jours' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, hostsRes, programsRes, statsRes] = await Promise.all([
          axios.get(`${API}/radio`),
          axios.get(`${API}/radio/hosts`),
          axios.get(`${API}/radio/programs`),
          axios.get(`${API}/radio/stats`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setContent(contentRes.data);
        setHosts(hostsRes.data);
        setPrograms(programsRes.data || []);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const refreshData = async () => {
    try {
      const [hostsRes, programsRes] = await Promise.all([
        axios.get(`${API}/radio/hosts`),
        axios.get(`${API}/radio/programs`)
      ]);
      setHosts(hostsRes.data);
      setPrograms(programsRes.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/radio`, content, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contenu radio mis à jour !');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHost = async () => {
    try {
      if (editingHost) {
        await axios.put(`${API}/radio/hosts/${editingHost.id}`, hostForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Animateur mis à jour');
      } else {
        const response = await axios.post(`${API}/radio/hosts`, hostForm, { headers: { Authorization: `Bearer ${token}` } });
        // Upload photo if selected
        if (photoFile) {
          const formData = new FormData();
          formData.append('file', photoFile);
          await axios.post(`${API}/upload/host-photo/${response.data.id}`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success('Animateur ajouté');
      }
      setDialogOpen(false);
      setEditingHost(null);
      setHostForm({ name: '', role: 'Animateur', bio: '' });
      setPhotoFile(null);
      refreshData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteHost = async (id) => {
    if (!window.confirm('Supprimer cet animateur ?')) return;
    try {
      await axios.delete(`${API}/radio/hosts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Animateur supprimé');
      refreshData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Programs functions
  const handleSaveProgram = async () => {
    try {
      if (editingProgram) {
        await axios.put(`${API}/radio/programs/${editingProgram.id}`, programForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Programme mis à jour');
      } else {
        await axios.post(`${API}/radio/programs`, programForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Programme ajouté');
      }
      setProgramDialogOpen(false);
      setEditingProgram(null);
      setProgramForm({ title: '', description: '', host_name: '', day: 'lundi', start_time: '09:00', end_time: '10:00', color: '#FACC15' });
      refreshData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Supprimer ce programme ?')) return;
    try {
      await axios.delete(`${API}/radio/programs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Programme supprimé');
      refreshData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditProgramDialog = (program) => {
    setEditingProgram(program);
    setProgramForm({
      title: program.title,
      description: program.description || '',
      host_name: program.host_name || '',
      day: program.day,
      start_time: program.start_time,
      end_time: program.end_time,
      color: program.color || '#FACC15'
    });
    setProgramDialogOpen(true);
  };

  const openNewProgramDialog = () => {
    setEditingProgram(null);
    setProgramForm({ title: '', description: '', host_name: '', day: 'lundi', start_time: '09:00', end_time: '10:00', color: '#FACC15' });
    setProgramDialogOpen(true);
  };

  const handleUploadPhoto = async (hostId) => {
    if (!photoFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      await axios.post(`${API}/upload/host-photo/${hostId}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploadée');
      setPhotoFile(null);
      refreshData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setUploading(false);
    }
  };

  const openEditDialog = (host) => {
    setEditingHost(host);
    setHostForm({ name: host.name, role: host.role, bio: host.bio || '' });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingHost(null);
    setHostForm({ name: '', role: 'Animateur', bio: '' });
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-['Teko'] text-4xl text-white">STUNT RADIO</h1>
          <p className="font-['Barlow'] text-gray-400 text-sm mt-1">Gérez le contenu, les animateurs et la grille des programmes</p>
        </div>
        <Button 
          onClick={handleSaveContent}
          disabled={saving}
          className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
        <Button
          variant={activeTab === 'content' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('content')}
          className={activeTab === 'content' ? 'bg-[#FACC15] text-black' : 'text-gray-400'}
        >
          Contenu
        </Button>
        <Button
          variant={activeTab === 'hosts' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('hosts')}
          className={activeTab === 'hosts' ? 'bg-[#FACC15] text-black' : 'text-gray-400'}
        >
          Animateurs ({hosts.length})
        </Button>
        <Button
          variant={activeTab === 'programs' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('programs')}
          className={activeTab === 'programs' ? 'bg-[#FACC15] text-black' : 'text-gray-400'}
        >
          Programmes ({programs.length})
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-8">
          {/* Stats - always visible */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-[#09090b] border border-white/10 p-4">
                <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Écoutes totales</p>
                <p className="font-['Teko'] text-3xl text-white">{stats.total_listens}</p>
              </div>
              <div className="bg-[#09090b] border border-white/10 p-4">
                <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Aujourd'hui</p>
                <p className="font-['Teko'] text-3xl text-[#FACC15]">{stats.listens_today}</p>
              </div>
              <div className="bg-[#09090b] border border-white/10 p-4">
                <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Cette semaine</p>
                <p className="font-['Teko'] text-3xl text-blue-400">{stats.listens_week}</p>
              </div>
              <div className="bg-[#09090b] border border-white/10 p-4">
                <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Ce mois</p>
                <p className="font-['Teko'] text-3xl text-green-400">{stats.listens_month}</p>
              </div>
              <div className="bg-[#09090b] border border-white/10 p-4">
                <p className="font-['Barlow'] text-gray-400 text-xs uppercase">Auditeurs uniques</p>
                <p className="font-['Teko'] text-3xl text-purple-400">{stats.unique_listeners}</p>
              </div>
            </div>
          )}

          {/* Content Settings Tab */}
          {activeTab === 'content' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#09090b] border border-white/10 p-6">
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Titre</Label>
                <Input
                  value={content.title}
                  onChange={(e) => setContent({ ...content, title: e.target.value })}
                  className="bg-black border-white/20 text-white"
                />
              </div>
              <div className="bg-[#09090b] border border-white/10 p-6">
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Slogan</Label>
                <Input
                  value={content.slogan}
                  onChange={(e) => setContent({ ...content, slogan: e.target.value })}
                  className="bg-black border-white/20 text-white"
                />
              </div>
              <div className="bg-[#09090b] border border-white/10 p-6 lg:col-span-2">
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Description</Label>
                <Textarea
                  value={content.description}
                  onChange={(e) => setContent({ ...content, description: e.target.value })}
                  className="bg-black border-white/20 text-white"
                  rows={4}
                  placeholder="Décrivez votre radio..."
                />
              </div>
              <div className="bg-[#09090b] border border-white/10 p-6 lg:col-span-2">
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">URL RadioKing</Label>
                <Input
                  value={content.radioking_url}
                  onChange={(e) => setContent({ ...content, radioking_url: e.target.value })}
                  className="bg-black border-white/20 text-white"
                />
              </div>
            </div>
          )}

          {/* Animateurs Tab */}
          {activeTab === 'hosts' && (
            <div className="bg-[#09090b] border border-white/10 p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="font-['Teko'] text-2xl text-white">ANIMATEURS</h2>
                  <p className="font-['Barlow'] text-gray-400 text-sm">Gérez les animateurs de la radio</p>
                </div>
                <Button onClick={openNewDialog} className="bg-[#FACC15] text-black font-bold">
                  <Plus size={18} className="mr-2" /> Ajouter
                </Button>
              </div>

            {hosts.length === 0 ? (
              <p className="text-gray-500 text-center py-8 font-['Barlow']">Aucun animateur</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hosts.map((host) => (
                  <div key={host.id} className="bg-black border border-white/10 p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-[#FACC15]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {host.photo_url ? (
                          <img src={getImageUrl(host.photo_url)} alt={host.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-['Teko'] text-2xl text-[#FACC15]">
                            {host.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-['Teko'] text-xl text-white truncate">{host.name}</h3>
                        <p className="font-['Barlow'] text-[#FACC15] text-xs uppercase">{host.role}</p>
                        {host.bio && (
                          <p className="font-['Barlow'] text-gray-400 text-xs mt-1 line-clamp-2">{host.bio}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(host)} className="flex-1 text-white border-white/20">
                        <Edit size={14} className="mr-1" /> Modifier
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteHost(host.id)} className="text-red-400 border-red-400/30 hover:bg-red-400/10">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    {/* Photo upload for existing host */}
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPhotoFile(e.target.files[0])}
                        className="bg-black border-white/20 text-white text-xs"
                      />
                      {photoFile && (
                        <Button
                          size="sm"
                          onClick={() => handleUploadPhoto(host.id)}
                          disabled={uploading}
                          className="w-full mt-2 bg-white/10 hover:bg-white/20 text-white text-xs"
                        >
                          {uploading ? 'Upload...' : 'Uploader photo'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Programs Tab */}
          {activeTab === 'programs' && (
            <div className="bg-[#09090b] border border-white/10 p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="font-['Teko'] text-2xl text-white">GRILLE DES PROGRAMMES</h2>
                  <p className="font-['Barlow'] text-gray-400 text-sm">Gérez les émissions de la radio</p>
                </div>
                <Button onClick={openNewProgramDialog} className="bg-[#FACC15] text-black font-bold">
                  <Plus size={18} className="mr-2" /> Ajouter
                </Button>
              </div>

              {programs.length === 0 ? (
                <p className="text-gray-500 text-center py-8 font-['Barlow']">Aucun programme</p>
              ) : (
                <div className="space-y-3">
                  {DAYS.map(day => {
                    const dayPrograms = programs.filter(p => p.day === day.value);
                    if (dayPrograms.length === 0) return null;
                    return (
                      <div key={day.value} className="border border-white/10">
                        <div className="bg-[#FACC15]/10 px-4 py-2">
                          <h3 className="font-['Teko'] text-lg text-white uppercase">{day.label}</h3>
                        </div>
                        <div className="divide-y divide-white/5">
                          {dayPrograms.sort((a, b) => a.start_time.localeCompare(b.start_time)).map(program => (
                            <div key={program.id} className="px-4 py-3 flex items-center justify-between hover:bg-white/5">
                              <div className="flex items-center gap-4">
                                <div 
                                  className="w-2 h-10 rounded"
                                  style={{ backgroundColor: program.color || '#FACC15' }}
                                />
                                <div>
                                  <p className="font-['Teko'] text-xl text-white">{program.title}</p>
                                  <p className="font-['Barlow'] text-gray-400 text-sm">
                                    {program.start_time} - {program.end_time}
                                    {program.host_name && <span className="ml-2 text-[#FACC15]">• {program.host_name}</span>}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEditProgramDialog(program)} className="text-white border-white/20">
                                  <Edit size={14} />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleDeleteProgram(program.id)} className="text-red-400 border-red-400/30 hover:bg-red-400/10">
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Dialog for adding/editing host */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#09090b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Teko'] text-2xl text-[#FACC15]">
              {editingHost ? 'MODIFIER ANIMATEUR' : 'NOUVEL ANIMATEUR'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-gray-400">Nom *</Label>
              <Input
                value={hostForm.name}
                onChange={(e) => setHostForm({ ...hostForm, name: e.target.value })}
                className="bg-black border-white/20 text-white mt-1"
                placeholder="Nom de l'animateur"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-400">Rôle</Label>
              <Input
                value={hostForm.role}
                onChange={(e) => setHostForm({ ...hostForm, role: e.target.value })}
                className="bg-black border-white/20 text-white mt-1"
                placeholder="Animateur, DJ, Co-animateur..."
              />
            </div>
            <div>
              <Label className="text-sm text-gray-400">Bio</Label>
              <Textarea
                value={hostForm.bio}
                onChange={(e) => setHostForm({ ...hostForm, bio: e.target.value })}
                className="bg-black border-white/20 text-white mt-1"
                rows={3}
                placeholder="Courte biographie..."
              />
            </div>
            {!editingHost && (
              <div>
                <Label className="text-sm text-gray-400">Photo</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files[0])}
                  className="bg-black border-white/20 text-white mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 text-white">
              Annuler
            </Button>
            <Button onClick={handleSaveHost} className="bg-[#FACC15] text-black font-bold">
              {editingHost ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for adding/editing program */}
      <Dialog open={programDialogOpen} onOpenChange={setProgramDialogOpen}>
        <DialogContent className="bg-[#09090b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Teko'] text-2xl text-[#FACC15]">
              {editingProgram ? 'MODIFIER PROGRAMME' : 'NOUVEAU PROGRAMME'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-gray-400">Titre de l'émission *</Label>
              <Input
                value={programForm.title}
                onChange={(e) => setProgramForm({ ...programForm, title: e.target.value })}
                className="bg-black border-white/20 text-white mt-1"
                placeholder="Ex: Morning Show"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-400">Animateur</Label>
              <Input
                value={programForm.host_name}
                onChange={(e) => setProgramForm({ ...programForm, host_name: e.target.value })}
                className="bg-black border-white/20 text-white mt-1"
                placeholder="Nom de l'animateur"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-400">Jour</Label>
              <select
                value={programForm.day}
                onChange={(e) => setProgramForm({ ...programForm, day: e.target.value })}
                className="w-full bg-black border border-white/20 text-white mt-1 p-2 rounded"
              >
                {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-400">Heure début</Label>
                <Input
                  type="time"
                  value={programForm.start_time}
                  onChange={(e) => setProgramForm({ ...programForm, start_time: e.target.value })}
                  className="bg-black border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-400">Heure fin</Label>
                <Input
                  type="time"
                  value={programForm.end_time}
                  onChange={(e) => setProgramForm({ ...programForm, end_time: e.target.value })}
                  className="bg-black border-white/20 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-400">Couleur</Label>
              <div className="flex gap-2 mt-1">
                {['#FACC15', '#22C55E', '#3B82F6', '#EF4444', '#8B5CF6', '#EC4899'].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setProgramForm({ ...programForm, color })}
                    className={`w-8 h-8 rounded ${programForm.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-black' : ''}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-400">Description</Label>
              <Textarea
                value={programForm.description}
                onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                className="bg-black border-white/20 text-white mt-1"
                rows={2}
                placeholder="Description de l'émission..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgramDialogOpen(false)} className="border-white/20 text-white">
              Annuler
            </Button>
            <Button onClick={handleSaveProgram} className="bg-[#FACC15] text-black font-bold">
              {editingProgram ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== TRAININGS MANAGER ==============
const TrainingsManager = ({ token }) => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    date: '',
    duration: '',
    max_participants: '',
    category: '',
    image_url: '',
  });

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainings(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    
    const data = new FormData();
    data.append('file', imageFile);
    
    const response = await axios.post(`${API}/upload`, data, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return `/api/files/${response.data.file_id}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      let imageUrl = formData.image_url;
      
      // Upload image if selected
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      
      const data = {
        ...formData,
        image_url: imageUrl,
        price: parseFloat(formData.price),
        max_participants: parseInt(formData.max_participants),
      };

      if (editingTraining) {
        await axios.put(`${API}/trainings/${editingTraining.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Formation mise à jour');
      } else {
        await axios.post(`${API}/trainings`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Formation créée');
      }
      setDialogOpen(false);
      setEditingTraining(null);
      resetForm();
      fetchTrainings();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      await axios.delete(`${API}/trainings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Formation supprimée');
      fetchTrainings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      date: '',
      duration: '',
      max_participants: '',
      category: '',
      image_url: '',
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleEdit = (training) => {
    setEditingTraining(training);
    setFormData({
      title: training.title,
      description: training.description,
      price: training.price.toString(),
      date: training.date,
      duration: training.duration,
      max_participants: training.max_participants.toString(),
      category: training.category,
      image_url: training.image_url || '',
    });
    setImageFile(null);
    setImagePreview(training.image_url ? getImageUrl(training.image_url) : null);
    setDialogOpen(true);
  };

  const getImageUrl = (url) => {
    if (url && url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const categories = ['Combat', 'Véhicules', 'Câblage', 'Pyrotechnie', 'Fondamentaux', 'Avancé'];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">FORMATIONS</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingTraining(null); resetForm(); }}}>
          <DialogTrigger asChild>
            <Button className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]" data-testid="add-training-btn">
              <Plus size={18} className="mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#09090b] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white">
                {editingTraining ? 'MODIFIER LA FORMATION' : 'NOUVELLE FORMATION'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Titre</Label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-black border-white/20 text-white"
                  data-testid="input-training-title"
                />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Description</Label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-black border-white/20 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Prix (€)</Label>
                  <Input
                    type="number"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="bg-black border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Catégorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="bg-black border-white/20 text-white">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#09090b] border-white/20">
                      {categories.map(c => (
                        <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Date</Label>
                  <Input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="bg-black border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Durée</Label>
                  <Input
                    required
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="bg-black border-white/20 text-white"
                    placeholder="Ex: 2 jours"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Places max</Label>
                  <Input
                    type="number"
                    required
                    value={formData.max_participants}
                    onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                    className="bg-black border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Image</Label>
                  <div className="border border-dashed border-white/20 hover:border-[#FACC15]/50 transition-colors p-3 text-center cursor-pointer relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    {imagePreview ? (
                      <div className="flex items-center gap-2">
                        <img src={imagePreview} alt="Preview" className="w-12 h-12 object-cover" />
                        <span className="text-[#FACC15] text-xs truncate flex-1">
                          {imageFile ? imageFile.name : 'Image actuelle'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Plus size={16} className="text-gray-500" />
                        <span className="text-gray-500 text-xs">Photo JPEG/PNG</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50" data-testid="save-training-btn">
                {uploading ? 'Upload en cours...' : (editingTraining ? 'Mettre à jour' : 'Créer la formation')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-4">
          {trainings.map((training) => (
            <div key={training.id} className="bg-[#09090b] border border-white/10 p-6 flex items-center justify-between" data-testid={`training-item-${training.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-['Teko'] text-xl text-white">{training.title}</h3>
                  <span className="text-xs px-2 py-1 bg-[#FACC15]/10 text-[#FACC15] font-['Barlow'] uppercase">{training.category}</span>
                  {!training.active && <span className="text-xs px-2 py-1 bg-[#EF4444]/10 text-[#EF4444] font-['Barlow'] uppercase">Inactif</span>}
                </div>
                <p className="font-['Barlow'] text-gray-400 text-sm mb-2">{training.description.substring(0, 100)}...</p>
                <div className="flex gap-4 text-sm text-gray-500 font-['Barlow']">
                  <span>{training.date}</span>
                  <span>{training.duration}</span>
                  <span>{training.current_participants}/{training.max_participants} places</span>
                  <span className="text-[#FACC15]">{training.price}€</span>
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <Button variant="ghost" size="icon" onClick={() => handleEdit(training)} className="text-gray-400 hover:text-[#FACC15]">
                  <Edit size={18} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(training.id)} className="text-gray-400 hover:text-[#EF4444]">
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          ))}
          {trainings.length === 0 && (
            <p className="text-center text-gray-500 py-12 font-['Barlow']">Aucune formation. Cliquez sur "Ajouter" pour créer la première.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============== GALLERY MANAGER ==============
const GalleryManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaType, setMediaType] = useState('photo'); // 'photo', 'video_youtube', 'video_upload'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'COMBATS',
    file: null,
    video_url: '',
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size for videos (15MB limit)
      if (mediaType === 'video_upload' && file.size > 15 * 1024 * 1024) {
        toast.error('Vidéo trop volumineuse ! Maximum 15 MB. Utilisez YouTube pour les vidéos plus longues.');
        return;
      }
      setFormData({...formData, file: file});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation selon le type de média
    if (mediaType === 'photo' && !formData.file) {
      toast.error('Veuillez sélectionner une photo');
      return;
    }
    if (mediaType === 'video_youtube' && !formData.video_url) {
      toast.error('Veuillez entrer une URL YouTube');
      return;
    }
    if (mediaType === 'video_upload' && !formData.file) {
      toast.error('Veuillez sélectionner une vidéo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      if (mediaType === 'photo' || mediaType === 'video_upload') {
        // Upload de fichier (photo ou vidéo)
        const data = new FormData();
        data.append('file', formData.file);
        data.append('title', formData.title || formData.file.name);
        data.append('description', formData.description);
        data.append('category', formData.category);

        await axios.post(`${API}/upload/gallery`, data, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });
      } else {
        // Ajout de vidéo YouTube
        await axios.post(`${API}/gallery`, {
          title: formData.title || 'Vidéo',
          description: formData.description,
          category: formData.category,
          type: 'video',
          url: formData.video_url,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Média ajouté avec succès !');
      setDialogOpen(false);
      setFormData({ title: '', description: '', category: 'COMBATS', file: null, video_url: '' });
      setMediaType('photo');
      setUploadProgress(0);
      fetchGallery();
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 413 || error.message?.includes('too large')) {
        toast.error('Fichier trop volumineux ! Maximum 15 MB pour les vidéos.');
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Élément supprimé');
      fetchGallery();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const categories = ['COMBATS', 'VÉHICULES', 'PYROTECHNIE', 'CÂBLAGE'];

  const getMediaUrl = (item) => {
    if (item.url && item.url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
    }
    return item.url;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">GALERIE</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]" data-testid="add-gallery-btn">
              <Plus size={18} className="mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#09090b] border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white">AJOUTER UN MÉDIA</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Media Type Selector */}
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                  Type de média
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    onClick={() => { setMediaType('photo'); setFormData({...formData, file: null, video_url: ''}); }}
                    className={`${mediaType === 'photo' 
                      ? 'bg-[#FACC15] text-black' 
                      : 'bg-transparent border border-white/20 text-white hover:border-[#FACC15]'}`}
                    data-testid="media-type-photo"
                  >
                    Photo
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { setMediaType('video_upload'); setFormData({...formData, file: null, video_url: ''}); }}
                    className={`${mediaType === 'video_upload' 
                      ? 'bg-[#FACC15] text-black' 
                      : 'bg-transparent border border-white/20 text-white hover:border-[#FACC15]'}`}
                    data-testid="media-type-video-upload"
                  >
                    Vidéo
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { setMediaType('video_youtube'); setFormData({...formData, file: null, video_url: ''}); }}
                    className={`${mediaType === 'video_youtube' 
                      ? 'bg-[#FACC15] text-black' 
                      : 'bg-transparent border border-white/20 text-white hover:border-[#FACC15]'}`}
                    data-testid="media-type-video-youtube"
                  >
                    YouTube
                  </Button>
                </div>
              </div>

              {/* Upload zone based on media type */}
              {mediaType === 'photo' && (
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Photo (JPG, PNG, WEBP)
                  </Label>
                  <div className="border-2 border-dashed border-white/20 hover:border-[#FACC15]/50 transition-colors p-6 text-center cursor-pointer relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      data-testid="file-upload-input"
                    />
                    {formData.file ? (
                      <div>
                        <p className="font-['Barlow'] text-[#FACC15] font-medium">{formData.file.name}</p>
                        <p className="font-['Barlow'] text-gray-400 text-sm mt-1">
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Plus size={32} className="mx-auto text-gray-500 mb-2" />
                        <p className="font-['Barlow'] text-gray-400">
                          Cliquez ou glissez une photo ici
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {mediaType === 'video_upload' && (
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Vidéo (MP4, MOV, WEBM) - Max 15 MB
                  </Label>
                  <div className="border-2 border-dashed border-white/20 hover:border-[#FACC15]/50 transition-colors p-6 text-center cursor-pointer relative">
                    <input
                      type="file"
                      accept="video/mp4,video/mov,video/webm,video/quicktime"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      data-testid="video-upload-input"
                    />
                    {formData.file ? (
                      <div>
                        <p className="font-['Barlow'] text-[#FACC15] font-medium">{formData.file.name}</p>
                        <p className={`font-['Barlow'] text-sm mt-1 ${formData.file.size > 15 * 1024 * 1024 ? 'text-red-500' : 'text-gray-400'}`}>
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                          {formData.file.size > 15 * 1024 * 1024 && ' - Trop volumineux !'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Plus size={32} className="mx-auto text-gray-500 mb-2" />
                        <p className="font-['Barlow'] text-gray-400">
                          Cliquez ou glissez une vidéo ici
                        </p>
                        <p className="font-['Barlow'] text-gray-500 text-xs mt-2">
                          Pour les vidéos longues, utilisez YouTube
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {mediaType === 'video_youtube' && (
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    URL YouTube
                  </Label>
                  <Input
                    value={formData.video_url}
                    onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                    placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..."
                    className="bg-black border-white/20 text-white"
                    data-testid="youtube-url-input"
                  />
                  <p className="font-['Barlow'] text-gray-500 text-xs mt-2">
                    Collez le lien de votre vidéo YouTube ou Shorts
                  </p>
                </div>
              )}

              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Titre</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  className="bg-black border-white/20 text-white" 
                  placeholder="Laisser vide pour utiliser le nom du fichier"
                />
              </div>

              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  className="bg-black border-white/20 text-white" 
                />
              </div>

              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-black border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#09090b] border-white/20">
                    {categories.map(c => (
                      <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {uploading && (
                <div className="bg-black p-4">
                  <div className="flex justify-between mb-2">
                    <span className="font-['Barlow'] text-gray-400 text-sm">Upload en cours...</span>
                    <span className="font-['Barlow'] text-[#FACC15] text-sm">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-[#18181b] h-2">
                    <div 
                      className="h-full bg-[#FACC15] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                disabled={uploading || (mediaType === 'photo' && !formData.file) || (mediaType === 'video_upload' && !formData.file) || (mediaType === 'video_youtube' && !formData.video_url) || (mediaType === 'video_upload' && formData.file && formData.file.size > 15 * 1024 * 1024)}
                className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50"
              >
                {uploading ? 'Ajout en cours...' : 'Ajouter le média'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className="relative group bg-[#09090b] border border-white/10 overflow-hidden" data-testid={`gallery-admin-item-${item.id}`}>
              {item.type === 'video' ? (
                <video 
                  src={getMediaUrl(item)} 
                  className="w-full aspect-video object-cover"
                  muted
                  onMouseOver={(e) => e.target.play()}
                  onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                />
              ) : (
                <img src={getMediaUrl(item)} alt={item.title} className="w-full aspect-video object-cover" />
              )}
              <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {item.type === 'video' && (
                  <span className="bg-[#FACC15] text-black px-2 py-1 text-xs font-bold uppercase">Vidéo</span>
                )}
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-white hover:text-[#EF4444]">
                  <Trash2 size={20} />
                </Button>
              </div>
              <div className="p-3">
                <p className="font-['Barlow'] text-white text-sm truncate">{item.title}</p>
                <div className="flex justify-between items-center">
                  <p className="font-['Barlow'] text-gray-500 text-xs">{item.category}</p>
                  <p className="font-['Barlow'] text-gray-600 text-xs uppercase">{item.type}</p>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="col-span-full text-center text-gray-500 py-12 font-['Barlow']">Aucun élément dans la galerie.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============== QUOTES MANAGER ==============
const QuotesManager = ({ token }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/quotes/${id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devis supprimé');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const statusColors = {
    pending: 'bg-[#FACC15]/10 text-[#FACC15]',
    contacted: 'bg-[#3B82F6]/10 text-[#3B82F6]',
    accepted: 'bg-[#22C55E]/10 text-[#22C55E]',
    rejected: 'bg-[#EF4444]/10 text-[#EF4444]',
  };

  return (
    <div>
      <h1 className="font-['Teko'] text-4xl text-white mb-8">DEMANDES DE DEVIS</h1>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-[#09090b] border border-white/10 p-6" data-testid={`quote-item-${quote.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-['Teko'] text-xl text-white">{quote.company_name}</h3>
                  <p className="font-['Barlow'] text-gray-400 text-sm">{quote.contact_name} - {quote.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 font-['Barlow'] uppercase ${statusColors[quote.status]}`}>
                  {quote.status}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-gray-500 font-['Barlow']">Type</p>
                  <p className="text-white font-['Barlow']">{quote.project_type}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-['Barlow']">Budget</p>
                  <p className="text-white font-['Barlow']">{quote.budget_range || 'Non spécifié'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-['Barlow']">Date souhaitée</p>
                  <p className="text-white font-['Barlow']">{quote.start_date || 'Non spécifié'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-['Barlow']">Téléphone</p>
                  <p className="text-white font-['Barlow']">{quote.phone}</p>
                </div>
              </div>
              <p className="font-['Barlow'] text-gray-300 text-sm mb-4">{quote.description}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => updateStatus(quote.id, 'contacted')} className="text-[#3B82F6] border-[#3B82F6]/30 hover:bg-[#3B82F6]/10">
                  Contacté
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateStatus(quote.id, 'accepted')} className="text-[#22C55E] border-[#22C55E]/30 hover:bg-[#22C55E]/10">
                  Accepté
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateStatus(quote.id, 'rejected')} className="text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/10">
                  Refusé
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteQuote(quote.id)} className="text-red-500 border-red-500/30 hover:bg-red-500/10 ml-auto">
                  <Trash2 size={16} className="mr-1" /> Supprimer
                </Button>
              </div>
            </div>
          ))}
          {quotes.length === 0 && (
            <p className="text-center text-gray-500 py-12 font-['Barlow']">Aucune demande de devis.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============== MESSAGES MANAGER ==============
const MessagesManager = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/messages/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${API}/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message supprimé');
      fetchMessages();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Supprimer TOUS les messages lus ? Cette action est irréversible.')) return;
    const readMessages = messages.filter(m => m.read);
    for (const msg of readMessages) {
      try {
        await axios.delete(`${API}/messages/${msg.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
    toast.success(`${readMessages.length} message(s) supprimé(s)`);
    fetchMessages();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Teko'] text-4xl text-white">MESSAGES</h1>
        {messages.filter(m => m.read).length > 0 && (
          <Button 
            variant="outline" 
            onClick={handleDeleteAll}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500"
          >
            <Trash2 size={16} className="mr-2" />
            Supprimer les lus ({messages.filter(m => m.read).length})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`bg-[#09090b] border p-6 ${message.read ? 'border-white/10' : 'border-[#FACC15]/30'}`}
              data-testid={`message-item-${message.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-['Teko'] text-xl text-white">{message.subject}</h3>
                    {!message.read && <span className="w-2 h-2 bg-[#FACC15] rounded-full"></span>}
                  </div>
                  <p className="font-['Barlow'] text-gray-400 text-sm">{message.name} - {message.email}</p>
                  <p className="font-['Barlow'] text-gray-500 text-xs mt-1">
                    {new Date(message.created_at).toLocaleDateString('fr-FR', { 
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                    })}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!message.read && (
                    <Button size="sm" variant="ghost" onClick={() => markAsRead(message.id)} className="text-gray-400 hover:text-[#FACC15]">
                      <Check size={16} className="mr-1" /> Lu
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(message.id)} className="text-gray-400 hover:text-red-500">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              <p className="font-['Barlow'] text-gray-300">{message.message}</p>
              <div className="mt-4">
                <a href={`mailto:${message.email}`} className="font-['Barlow'] text-[#FACC15] text-sm hover:underline">
                  Répondre par email →
                </a>
              </div>
            </div>
          ))}
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Mail size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500 font-['Barlow']">Aucun message.</p>
              <p className="text-gray-600 font-['Barlow'] text-sm mt-2">Les messages envoyés via le formulaire de contact apparaîtront ici.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== SETTINGS MANAGER ==============
const SettingsManager = ({ token }) => {
  const [settings, setSettings] = useState({
    hero_title: 'STUNT DIRECTOR',
    hero_subtitle: 'COORDINATION & RÉGLAGE DE CASCADES',
    about_text: '',
    services_text: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="font-['Teko'] text-4xl text-white mb-8">PARAMÈTRES DU SITE</h1>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="max-w-2xl space-y-6">
          <div className="bg-[#09090b] border border-white/10 p-6">
            <h2 className="font-['Teko'] text-xl text-white mb-4">SECTION HERO</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Titre principal</Label>
                <Input
                  value={settings.hero_title}
                  onChange={(e) => setSettings({...settings, hero_title: e.target.value})}
                  className="bg-black border-white/20 text-white"
                  data-testid="input-hero-title"
                />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Sous-titre</Label>
                <Input
                  value={settings.hero_subtitle}
                  onChange={(e) => setSettings({...settings, hero_subtitle: e.target.value})}
                  className="bg-black border-white/20 text-white"
                />
              </div>
            </div>
          </div>

          <div className="bg-[#09090b] border border-white/10 p-6">
            <h2 className="font-['Teko'] text-xl text-white mb-4">TEXTES</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">À propos</Label>
                <Textarea
                  value={settings.about_text}
                  onChange={(e) => setSettings({...settings, about_text: e.target.value})}
                  className="bg-black border-white/20 text-white min-h-[100px]"
                  placeholder="Texte de présentation..."
                />
              </div>
              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Services</Label>
                <Textarea
                  value={settings.services_text}
                  onChange={(e) => setSettings({...settings, services_text: e.target.value})}
                  className="bg-black border-white/20 text-white min-h-[100px]"
                  placeholder="Description des services..."
                />
              </div>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]"
            data-testid="save-settings-btn"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
